import * as React from 'react';
import {Link} from 'react-router-dom';
import LegalPage, {LEGAL_ADDRESS, LEGAL_ENTITY} from '../components/LegalPage';
import {BRAND} from '../brand';

const PrivacyPolicyPage: React.FC = () => (
    <LegalPage eyebrow="Legal" title="Privacy Policy" updated="October 22, 2026">
        <section>
            <p>
                This policy explains what {LEGAL_ENTITY} ("{BRAND.name}", "we") collects when you use this website,
                why, and the choices you have. We keep it short because we collect very little.
            </p>
        </section>

        <section>
            <h2>What we collect</h2>
            <ul>
                <li>
                    <strong>When you sign in with Facebook:</strong> your name, email address, profile photo, and the
                    app-specific Facebook ID Facebook gives us. If you grant it, we also read the list of Facebook Pages
                    you manage, only to check whether you manage the Love Wins Media Page (which gives staff access).
                    We never post to Facebook or read your messages, friends, or timeline.
                </li>
                <li>
                    <strong>When you book gear or start a project:</strong> the contact details you type in (name, email,
                    phone, company) and the details of your request.
                </li>
                <li>
                    <strong>Basic technical data:</strong> a session cookie to keep you signed in, and standard server
                    logs (IP address, browser, pages requested) used for security and troubleshooting.
                </li>
                <li>
                    <strong>Google Analytics:</strong> with your permission, to count public page visits. It sets
                    first-party cookies and receives your IP address and browser details. We don't link it to your
                    account or use it for advertising.
                </li>
            </ul>
        </section>

        <section>
            <h2>Optional analytics</h2>
            <p>With your permission, we use Google Analytics to measure public page visits and successful booking
                requests. Google may process device and browser information and analytics cookie identifiers.
                We do not send form contents, account details, URL query parameters or private client and staff
                page views. Advertising personalization is disabled. You can decline or withdraw permission
                using Analytics preferences on public pages. We also respect Do Not Track and Global Privacy Control.</p>
        </section>

        <section>
            <h2>How we use it</h2>
            <ul>
                <li>To sign you in and show you your reservations, projects, and quotes.</li>
                <li>To respond to your requests, prepare quotes, and run rentals and productions you book with us.</li>
                <li>To keep the site secure and working.</li>
            </ul>
            <p className="mt-3">We don't sell your information or use it for advertising.</p>
        </section>

        <section>
            <h2>Who we share it with</h2>
            <p>
                Only the service providers that run this site for us (hosting, database, email, and payment
                processing when you pay, and Google for site analytics), and only as needed to provide those services, or where the law requires it.
            </p>
        </section>

        <section>
            <h2>How long we keep it</h2>
            <p>
                Account details are kept while your account is active. Booking and billing records are kept as long
                as we need them for our business and legal obligations (for example, tax records). You can ask us to
                delete your data at any time; see <Link to="/data-deletion">Data Deletion</Link>.
            </p>
        </section>

        <section>
            <h2>Your choices</h2>
            <ul>
                <li>You can ask to see, correct, or delete the information we hold about you.</li>
                <li>
                    You can remove our app's access from Facebook at any time under Settings &amp; privacy →
                    Settings → Apps and websites.
                </li>
                <li>
                    You can opt out of Google Analytics with Google's{' '}
                    <a href="https://tools.google.com/dlpage/gaoptout">browser add-on</a>.
                </li>
            </ul>
        </section>

        <section>
            <h2>Contact</h2>
            <p>
                Questions or requests: reach us through our <Link to="/contact">contact page</Link>, or write to
                {' '}{LEGAL_ENTITY}, {LEGAL_ADDRESS}.
            </p>
            <p className="mt-3">
                If this policy changes in a meaningful way, we'll update the date at the top of this page.
            </p>
        </section>
    </LegalPage>
);

export default PrivacyPolicyPage;
